

import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const COLOR_OPTIONS = ['#16A34A','#DC2626','#D97706','#B45309','#DB2777','#059669','#7C3AED','#0891B2','#92400E','#6B7280','#1D4ED8','#0F766E'];
const EMOJI_OPTIONS = ['🥦','🍎','','🌶️','🌸','🥬','🫘',,'🍄','🍅','🥕','🧅','🧄','🌽','🥒','🍆','🫑','🎃','🫛','🥭','🍌','🍇','🍊','🍋','🥥','🍈','🍉','🍍','🌼','🌹','🌻','🪷','🌺','💐'];
const EMPTY_CAT = { name:'', icon:'🌱', color:'#16A34A', image:'' };

// ── Reusable image uploader ──────────────────────────────────
function ImageUploader({ value, onChange, size = 64, label = 'Upload Image' }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate type & size
    if (!file.type.startsWith('image/')) return toast.error('Only image files allowed');
    if (file.size > 2 * 1024 * 1024) return toast.error('Image must be under 2MB');

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const { data } = await api.post('/upload/media', fd);
      onChange(data.files[0].url);
      toast.success('Image uploaded ✅');
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      {/* Preview */}
      <div style={{
        width: size, height: size, borderRadius:10, border:'2px dashed #dee2e6',
        overflow:'hidden', background:'#f8f9fa', display:'flex',
        alignItems:'center', justifyContent:'center', flexShrink:0,
      }}>
        {value
          ? <img src={value} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <span style={{ fontSize:22, color:'#adb5bd' }}>🖼️</span>
        }
      </div>

      <div style={{ flex:1 }}>
        <label style={{
          display:'inline-flex', alignItems:'center', gap:6,
          padding:'7px 14px', background:'#1A6B3C', color:'white',
          borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
          opacity: uploading ? 0.7 : 1,
        }}>
          {uploading
            ? <><span className="spinner spinner--sm spinner--white" /> Uploading...</>
            : <><span>📤</span> {label}</>}
          <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleFile} disabled={uploading} />
        </label>
        {value && (
          <button onClick={() => onChange('')}
            style={{ marginLeft:8, fontSize:11, color:'#DC2626', background:'none', border:'none', cursor:'pointer' }}>
            ✕ Remove
          </button>
        )}
        <div style={{ fontSize:11, color:'#6C757D', marginTop:3 }}>JPG, PNG, WebP · max 2MB</div>
      </div>
    </div>
  );
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [tab, setTab]               = useState('list');
  const [catForm, setCatForm]       = useState(EMPTY_CAT);
  const [subForm, setSubForm]       = useState({ name:'', icon:'', image:'' });
  const [saving, setSaving]         = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [editSub, setEditSub]       = useState(null);
  const [searchCat, setSearchCat]   = useState('');
  const [searchSub, setSearchSub]   = useState('');
  const [dragIdx, setDragIdx]           = useState(null);
  const [dragOverIdx, setDragOverIdx]   = useState(null);
  const [catDragIdx, setCatDragIdx]     = useState(null);
  const [catDragOver, setCatDragOver]   = useState(null);
  const [editCatMode, setEditCatMode]   = useState(false);
  const [editCatForm, setEditCatForm]   = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/categories');
      setCategories(data.categories || []);
      if (data.categories?.length && !selected) setSelected(data.categories[0]);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const setF  = (k,v) => setCatForm(p => ({...p,[k]:v}));
  const setSF = (k,v) => setSubForm(p => ({...p,[k]:v}));
  const setEF = (k,v) => setEditCatForm(p => ({...p,[k]:v}));

  const saveEditCat = async () => {
    if (!editCatForm.name.trim()) return toast.error('Category name required');
    setSaving(true);
    try {
      await updateCategory(selected, editCatForm);
      setEditCatMode(false);
      toast.success('Category updated');
    } finally { setSaving(false); }
  };

  const addCategory = async () => {
    if (!catForm.name.trim()) return toast.error('Category name required');
    setSaving(true);
    try {
      const { data } = await api.post('/admin/categories', catForm);
      setCategories(p => [...p, data.category]);
      setSelected(data.category);
      setCatForm(EMPTY_CAT);
      setTab('list');
      toast.success(data.category.name + ' added');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const updateCategory = async (cat, updates) => {
    try {
      const { data } = await api.put('/admin/categories/' + cat._id, updates);
      setCategories(p => p.map(c => c._id===cat._id ? data.category : c));
      if (selected?._id===cat._id) setSelected(data.category);
    } catch { toast.error('Update failed'); }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm('Delete "' + cat.name + '" and all sub-categories?')) return;
    try {
      await api.delete('/admin/categories/' + cat._id);
      const updated = categories.filter(c => c._id !== cat._id);
      setCategories(updated);
      setSelected(updated[0] || null);
      toast.success('Deleted');
    } catch { toast.error('Error'); }
  };

  const addSub = async () => {
    if (!subForm.name.trim() || !selected) return toast.error('Name required');
    setSaving(true);
    try {
      const { data } = await api.post('/admin/categories/' + selected._id + '/subcategories', subForm);
      setCategories(p => p.map(c => c._id===selected._id ? data.category : c));
      setSelected(data.category);
      setSubForm({ name:'', icon:'', image:'' });
      setShowAddSub(false);
      toast.success(subForm.name + ' added');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const updateSub = async (subId, updates) => {
    try {
      const { data } = await api.put('/admin/categories/' + selected._id + '/subcategories/' + subId, updates);
      setCategories(p => p.map(c => c._id===selected._id ? data.category : c));
      setSelected(data.category);
      setEditSub(null);
      toast.success('Updated');
    } catch { toast.error('Error'); }
  };

  const deleteSub = async (subId, name) => {
    if (!window.confirm('Remove "' + name + '"?')) return;
    try {
      const { data } = await api.delete('/admin/categories/' + selected._id + '/subcategories/' + subId);
      setCategories(p => p.map(c => c._id===selected._id ? data.category : c));
      setSelected(data.category);
      toast.success('Removed');
    } catch { toast.error('Error'); }
  };

  // ── Reorder subcategory up or down ──────────────────────────
  const reorderSub = async (subId, direction) => {
    if (!selected) return;
    const subs = [...(selected.subCategories || [])];
    const idx  = subs.findIndex(s => (s._id?.toString ? s._id.toString() : s._id) === (subId?.toString ? subId.toString() : subId));
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= subs.length) return;

    // Swap
    [subs[idx], subs[newIdx]] = [subs[newIdx], subs[idx]];

    // Optimistic UI update
    const updated = { ...selected, subCategories: subs };
    setSelected(updated);
    setCategories(p => p.map(c => c._id === selected._id ? updated : c));

    saveSubOrder(subs);
  };

  // ── Save subcategory order to backend ───────────────────────
  const saveSubOrder = async (subs) => {
    const updated = { ...selected, subCategories: subs };
    setSelected(updated);
    setCategories(p => p.map(c => c._id === selected._id ? updated : c));
    try {
      const { data } = await api.put('/admin/categories/' + selected._id, { subCategories: subs });
      if (data?.category) { setSelected(data.category); setCategories(p => p.map(c => c._id === selected._id ? data.category : c)); }
      toast.success('Order saved ✅');
    } catch { toast.error('Failed to save order'); load(); }
  };

  // ── Save category order to backend ─────────────────────────
  const saveCatOrder = async (cats) => {
    setCategories(cats);
    try {
      await Promise.all(cats.map((cat, idx) =>
        api.put('/admin/categories/' + cat._id, { order: idx })
      ));
      toast.success('Category order saved ✅');
    } catch { toast.error('Failed to save order'); load(); }
  };

  const filteredCats = categories.filter(c => c.name.toLowerCase().includes(searchCat.toLowerCase()));
  const filteredSubs = selected ? (selected.subCategories||[]).filter(s => s.name.toLowerCase().includes(searchSub.toLowerCase())) : [];
  const totalSubs = categories.reduce((s,c) => s+(c.subCategories?.length||0), 0);

  return (
    <AdminLayout title="Category Management">
      <div className="stat-grid" style={{ gridTemplateColumns:"repeat(4,1fr)", marginBottom:20 }}>
        {[
          { label:"Total Categories", value:categories.length,                          icon:"📂", cls:"stat-card--green" },
          { label:"Sub-categories",   value:totalSubs,                                  icon:"🏷️", cls:"stat-card--blue"  },
          { label:"Active",           value:categories.filter(c=>c.isActive).length,   icon:"✅", cls:"stat-card--amber" },
          { label:"Disabled",         value:categories.filter(c=>!c.isActive).length,  icon:"⛔", cls:"stat-card--red"   },
        ].map(s => (
          <div key={s.label} className={"stat-card " + s.cls}>
            <div className="stat-card__icon">{s.icon}</div>
            <div className="stat-card__label">{s.label}</div>
            <div className="stat-card__value">{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:20 }}>

        {/* LEFT — category list */}
        <div>
          <div className="flex-between" style={{ marginBottom:10 }}>
            <span style={{ fontWeight:600, fontSize:13 }}>Categories</span>
            <button className="btn btn--primary btn--sm" onClick={() => { setTab("add"); setCatForm(EMPTY_CAT); }}>➕ Add</button>
          </div>
          <input className="input" placeholder="Search categories..." value={searchCat}
            onChange={e => setSearchCat(e.target.value)} style={{ marginBottom:10 }} />
          {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {filteredCats.length === 0 && (
                <div style={{ textAlign:"center", padding:20, color:"#6C757D", fontSize:13 }}>
                  No categories yet.<br/>Click ➕ Add to create.
                </div>
              )}
              {filteredCats.map((cat, idx) => {
                const isDragging = catDragIdx === idx;
                const isDragOver = catDragOver === idx;
                return (
                <div key={cat._id}
                  draggable={true}
                  onClick={() => { setSelected(cat); setTab("list"); setSearchSub(""); setEditCatMode(false); }}
                  onDragStart={e => { e.dataTransfer.effectAllowed='move'; setCatDragIdx(idx); }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect='move'; if(catDragOver!==idx) setCatDragOver(idx); }}
                  onDragLeave={() => setCatDragOver(null)}
                  onDrop={e => {
                    e.preventDefault();
                    if (catDragIdx === null || catDragIdx === idx) return;
                    const reordered = [...filteredCats];
                    const [moved] = reordered.splice(catDragIdx, 1);
                    reordered.splice(idx, 0, moved);
                    setCatDragIdx(null); setCatDragOver(null);
                    saveCatOrder(reordered);
                  }}
                  onDragEnd={() => { setCatDragIdx(null); setCatDragOver(null); }}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    border:"1px solid "+(isDragOver ? cat.color||"#1A6B3C" : selected?._id===cat._id ? cat.color||"#1A6B3C" : "#dee2e6"),
                    background: isDragging ? '#f0f0f0' : isDragOver ? (cat.color||"#1A6B3C")+"20" : selected?._id===cat._id ? (cat.color||"#1A6B3C")+"15" : "white",
                    opacity: isDragging ? 0.4 : cat.isActive ? 1 : 0.5,
                    transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
                    transition:'transform 0.15s, border 0.15s',
                    boxShadow: isDragOver ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                  }}>
                  <span style={{ fontSize:14, color:'#adb5bd', cursor:'grab', flexShrink:0 }} title="Drag to reorder">⠿</span>
                  <div style={{ width:40, height:40, borderRadius:8, background:(cat.color||"#1A6B3C")+"20",
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0, overflow:"hidden" }}>
                    {cat.image
                      ? <img src={cat.image} alt={cat.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => e.target.style.display="none"} />
                      : (cat.icon||"")}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cat.name}</div>
                    <div style={{ fontSize:11, color:"#6C757D" }}>{cat.subCategories?.length||0} items</div>
                  </div>
                  {!cat.isActive && <span className="badge badge--danger" style={{ fontSize:9 }}>OFF</span>}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div>

          {/* ── ADD CATEGORY ── */}
          {tab === "add" && (
            <div className="card">
              <div className="card__header">
                <div className="card__title">➕ Add New Category</div>
                <button className="btn btn--ghost btn--sm" onClick={() => setTab("list")}>← Back</button>
              </div>
              <div className="card__body">
                <div className="form-group">
                  <label className="label">Category Name *</label>
                  <input className="input" placeholder="e.g. Vegetables, Fruits, Flowers..." autoFocus
                    value={catForm.name} onChange={e => setF("name", e.target.value)} />
                </div>

                {/* Upload instead of URL */}
                <div className="form-group">
                  <label className="label">Category Image (optional)</label>
                  <ImageUploader
                    value={catForm.image}
                    onChange={url => setF("image", url)}
                    size={72}
                    label="Upload Image"
                  />
                  <div style={{ fontSize:11, color:'#6C757D', marginTop:4 }}>
                    Leave empty to use an emoji icon instead
                  </div>
                </div>

                {!catForm.image && (
                  <div className="form-group">
                    <label className="label">Icon (used when no image)</label>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                      {EMOJI_OPTIONS.map(em => (
                        <button key={em} onClick={() => setF("icon", em)}
                          style={{ width:38, height:38, fontSize:20, border:"2px solid "+(catForm.icon===em?catForm.color:"#dee2e6"),
                            borderRadius:8, background:catForm.icon===em?catForm.color+"20":"white", cursor:"pointer" }}>
                          {em}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="label">Theme Color</label>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {COLOR_OPTIONS.map(c => (
                      <button key={c} onClick={() => setF("color", c)}
                        style={{ width:30, height:30, borderRadius:"50%", background:c,
                          border:catForm.color===c?"3px solid #212529":"3px solid transparent", cursor:"pointer" }} />
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"#f8f9fa", borderRadius:8, marginBottom:16 }}>
                  <div style={{ width:52, height:52, borderRadius:10, background:catForm.color+"20", display:"flex", alignItems:"center",
                    justifyContent:"center", fontSize:28, overflow:"hidden", border:"2px solid "+catForm.color+"40" }}>
                    {catForm.image
                      ? <img src={catForm.image} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : catForm.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, color:catForm.color }}>{catForm.name||"Category preview"}</div>
                    <div style={{ fontSize:12, color:"#6C757D" }}>0 sub-categories</div>
                  </div>
                </div>

                <div style={{ display:"flex", gap:10 }}>
                  <button className="btn btn--outline" onClick={() => setTab("list")}>Cancel</button>
                  <button className="btn btn--primary" onClick={addCategory} disabled={saving}>
                    {saving ? <span className="spinner spinner--sm spinner--white" /> : "Create Category"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── SUB-CATEGORIES ── */}
          {tab === "list" && selected && (
            <>
              <div className="card" style={{ marginBottom:16 }}>
                <div className="card__body--sm">
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:48, height:48, borderRadius:10, background:(selected.color||"#1A6B3C")+"20", display:"flex",
                      alignItems:"center", justifyContent:"center", fontSize:26, overflow:"hidden", flexShrink:0 }}>
                      {selected.image
                        ? <img src={selected.image} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                        : (selected.icon||"")}
                    </div>
                    <div style={{ flex:1 }}>
                      <input style={{ border:"none", outline:"none", fontSize:16, fontWeight:700, color:selected.color||"#212529", background:"transparent", width:"100%" }}
                        value={selected.name} onChange={e => setSelected(p=>({...p,name:e.target.value}))}
                        onBlur={e => updateCategory(selected, { name:e.target.value })} />
                      <div style={{ fontSize:12, color:"#6C757D" }}>{filteredSubs.length} sub-categories</div>
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <button className="btn btn--outline btn--sm"
                        onClick={() => {
                          setEditCatForm({ name:selected.name, icon:selected.icon||'🌱', color:selected.color||'#16A34A', image:selected.image||'' });
                          setEditCatMode(v => !v);
                        }}>
                        ✏️ Edit
                      </button>
                      <button className={"btn btn--sm "+(selected.isActive?"btn--warning":"btn--success")}
                        onClick={() => updateCategory(selected, { isActive:!selected.isActive })}>
                        {selected.isActive ? "⛔ Disable" : "✅ Enable"}
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => deleteCategory(selected)}>🗑</button>
                    </div>
                  </div>

                  {/* ── Edit category panel ── */}
                  {editCatMode && editCatForm && (
                    <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid #dee2e6" }}>
                      <div className="form-group">
                        <label className="label">Category Image (optional)</label>
                        <ImageUploader
                          value={editCatForm.image}
                          onChange={url => setEF("image", url)}
                          size={64}
                          label="Upload Image"
                        />
                      </div>

                      {!editCatForm.image && (
                        <div className="form-group">
                          <label className="label">Icon (used when no image)</label>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {EMOJI_OPTIONS.map(em => (
                              <button key={em} onClick={() => setEF("icon", em)}
                                style={{ width:34, height:34, fontSize:18, border:"2px solid "+(editCatForm.icon===em?editCatForm.color:"#dee2e6"),
                                  borderRadius:8, background:editCatForm.icon===em?editCatForm.color+"20":"white", cursor:"pointer" }}>
                                {em}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="form-group">
                        <label className="label">Theme Color</label>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          {COLOR_OPTIONS.map(c => (
                            <button key={c} onClick={() => setEF("color", c)}
                              style={{ width:28, height:28, borderRadius:"50%", background:c,
                                border:editCatForm.color===c?"3px solid #212529":"3px solid transparent", cursor:"pointer" }} />
                          ))}
                        </div>
                      </div>

                      <div style={{ display:"flex", gap:10, marginTop:4 }}>
                        <button className="btn btn--primary btn--sm" onClick={saveEditCat} disabled={saving}>
                          {saving ? <span className="spinner spinner--sm spinner--white" /> : "✅ Save"}
                        </button>
                        <button className="btn btn--outline btn--sm" onClick={() => setEditCatMode(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card__header">
                  <div className="card__title">🏷️ Sub-categories</div>
                  <button className="btn btn--primary btn--sm" onClick={() => { setShowAddSub(true); setSubForm({ name:"", icon:"", image:"" }); }}>➕ Add</button>
                </div>
                <div className="card__body--sm">
                  <input className="input" placeholder="Search sub-categories..." value={searchSub}
                    onChange={e => setSearchSub(e.target.value)} style={{ marginBottom:12 }} />

                  {/* ── Add sub form ── */}
                  {showAddSub && (
                    <div style={{ background:"#f8f9fa", borderRadius:8, padding:12, marginBottom:12, border:"1px solid #dee2e6" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                        <div>
                          <label className="label">Name *</label>
                          <input className="input" placeholder="e.g. Tomato" autoFocus
                            value={subForm.name} onChange={e => setSF("name", e.target.value)}
                            onKeyDown={e => e.key==="Enter" && addSub()} />
                        </div>
                        <div>
                          <label className="label">Image (optional)</label>
                          <ImageUploader
                            value={subForm.image}
                            onChange={url => setSF("image", url)}
                            size={48}
                            label="Upload"
                          />
                        </div>
                      </div>
                      {!subForm.image && (
                        <div style={{ marginBottom:8 }}>
                          <label className="label">Icon (fallback)</label>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                            {EMOJI_OPTIONS.slice(0,20).map(em => (
                              <button key={em} onClick={() => setSF("icon", em)}
                                style={{ width:30, height:30, fontSize:15, border:"2px solid "+(subForm.icon===em?selected.color:"#dee2e6"),
                                  borderRadius:6, background:subForm.icon===em?selected.color+"20":"white", cursor:"pointer" }}>
                                {em}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ display:"flex", gap:8 }}>
                        <button className="btn btn--primary btn--sm" onClick={addSub} disabled={saving}>
                          {saving ? <span className="spinner spinner--sm spinner--white" /> : "✅ Add"}
                        </button>
                        <button className="btn btn--outline btn--sm" onClick={() => setShowAddSub(false)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {filteredSubs.length === 0 && !showAddSub ? (
                    <div className="empty-state" style={{ padding:"24px 0" }}>
                      <div className="empty-state__icon">🏷️</div>
                      <div className="empty-state__title">No sub-categories yet</div>
                      <div className="empty-state__desc">Add items like Tomato, Carrot, Rose...</div>
                    </div>
                  ) : (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                      {filteredSubs.map((sub, filtIdx) => {
                        const isDragging = dragIdx === filtIdx;
                        const isDragOver = dragOverIdx === filtIdx;
                        return (
                        <div key={sub._id?.toString?.() || filtIdx}
                          draggable={true}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            setDragIdx(filtIdx);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverIdx !== filtIdx) setDragOverIdx(filtIdx);
                          }}
                          onDragLeave={() => setDragOverIdx(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (dragIdx === null || dragIdx === filtIdx) return;
                            const allSubs = [...filteredSubs];
                            const [moved] = allSubs.splice(dragIdx, 1);
                            allSubs.splice(filtIdx, 0, moved);
                            setDragIdx(null);
                            setDragOverIdx(null);
                            saveSubOrder(allSubs);
                          }}
                          onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                          style={{
                            borderRadius:10,
                            border: isDragOver ? '2px solid '+(selected.color||'#1A6B3C') : '1px solid '+(selected.color||'#1A6B3C')+'25',
                            background: isDragging ? '#f0f0f0' : isDragOver ? (selected.color||'#1A6B3C')+'20' : (selected.color||'#1A6B3C')+'08',
                            overflow:'hidden', cursor:'grab', opacity: isDragging ? 0.4 : 1,
                            transform: isDragOver ? 'scale(1.04)' : 'scale(1)',
                            transition:'transform 0.15s, border 0.15s, opacity 0.15s',
                            boxShadow: isDragOver ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                          }}>
                          {editSub?._id === sub._id ? (
                            <div style={{ padding:8 }}>
                              <input className="input" value={editSub.name}
                                onChange={e => setEditSub(p=>({...p,name:e.target.value}))}
                                style={{ marginBottom:6, fontSize:12 }} />
                              <div style={{ marginBottom:8 }}>
                                <ImageUploader
                                  value={editSub.image||""}
                                  onChange={url => setEditSub(p=>({...p,image:url}))}
                                  size={48}
                                  label="Change"
                                />
                              </div>
                              <div style={{ display:"flex", gap:4 }}>
                                <button onClick={() => updateSub(sub._id, editSub)}
                                  style={{ flex:1, background:selected.color||"#1A6B3C", color:"white", border:"none", borderRadius:5, padding:"4px", fontSize:11, cursor:"pointer" }}>✅ Save</button>
                                <button onClick={() => setEditSub(null)}
                                  style={{ flex:1, background:"#f1f3f5", color:"#6C757D", border:"none", borderRadius:5, padding:"4px", fontSize:11, cursor:"pointer" }}>✕ Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ height:80, background:(selected.color||"#1A6B3C")+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, overflow:"hidden", position:"relative" }}>
                                <div style={{ position:"absolute", top:4, right:6, fontSize:12, color:(selected.color||"#1A6B3C"), opacity:0.5 }} title="Drag to reorder">⠿</div>
                                {sub.image
                                  ? <img src={sub.image} alt={sub.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                                      onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                                  : null}
                                <span style={{ display: sub.image ? 'none' : 'flex' }}>{sub.icon||""}</span>
                              </div>
                              <div style={{ padding:"6px 8px" }}>
                                <div style={{ fontSize:12, fontWeight:600, textAlign:"center", marginBottom:4 }}>{sub.name}</div>
                                <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                                  <button onClick={() => reorderSub(sub._id, 'up')}
                                    title="Move up"
                                    style={{ flex:1, background:"white", color:"#1A6B3C", border:"1px solid #dee2e6", borderRadius:5, padding:"3px", fontSize:13, cursor:"pointer" }}>↑</button>
                                  <button onClick={() => reorderSub(sub._id, 'down')}
                                    title="Move down"
                                    style={{ flex:1, background:"white", color:"#1A6B3C", border:"1px solid #dee2e6", borderRadius:5, padding:"3px", fontSize:13, cursor:"pointer" }}>↓</button>
                                </div>
                                <div style={{ display:"flex", gap:4 }}>
                                  <button onClick={() => setEditSub({...sub})}
                                    style={{ flex:1, background:"white", color:"#6C757D", border:"1px solid #dee2e6", borderRadius:5, padding:"3px", fontSize:11, cursor:"pointer" }}>✏️</button>
                                  <button onClick={() => deleteSub(sub._id, sub.name)}
                                    style={{ flex:1, background:"white", color:"#DC2626", border:"1px solid #FEE2E2", borderRadius:5, padding:"3px", fontSize:11, cursor:"pointer" }}>🗑</button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === "list" && !selected && !loading && (
            <div className="empty-state">
              <div className="empty-state__icon">📂</div>
              <div className="empty-state__title">No categories yet</div>
              <div className="empty-state__desc">Create your first category to get started</div>
              <button className="btn btn--primary" style={{ marginTop:12 }} onClick={() => { setTab("add"); setCatForm(EMPTY_CAT); }}>➕ Add Category</button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}